import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useConditionalFetch } from '@/composables'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import {
  useWalletStore,
  useAccountAPIStore,
  useAccountTokenAPIStore,
  useCoinPriceAPIStore,
  useCoinAPIStore,
  useUserStore,
  useInfoAPIStore,
  useAccountBalanceAPIStore,
} from '@/stores'
import { type BaseAccount } from '@/types'
import type {
  IWallet,
  IWalletKeyInfo,
  ICreateWalletRequest,
  IBackupWalletRequest,
  IUpdateWalletRequest,
  IGetWalletListRequest,
  IDeleteWalletRequest,
  IChangePinRequest,
} from '@/types/api/wallet'
import { getExecuteOnce } from '@/utils'

export interface APIStoreDeleteWalletOptions {
  deleteInMemory?: boolean
}

// IWalletKeyInfo는 types/api/wallet.ts에서 re-export
export type { IWalletKeyInfo }

const useWalletAPIStore = defineStore('walletAPI', () => {
  const accountAPIStore = useAccountAPIStore()
  const accountTokenAPIStore = useAccountTokenAPIStore()
  const coinAPIStore = useCoinAPIStore()
  const coinPriceAPIStore = useCoinPriceAPIStore()
  const infoAPIStore = useInfoAPIStore()
  const walletStore = useWalletStore()
  const userStore = useUserStore()

  const walletList = ref<IWallet[]>([])
  // 각 wallet의 key info (잠금 상태, 남은 시간 등)를 저장
  const walletKeyInfoMap = ref<Record<string, IWalletKeyInfo>>({})

  const createWallet = async (params: ICreateWalletRequest) => {
    if (typeof walletList.value === 'undefined') {
      walletList.value = []
    }
    const res = await WepinBackendAPI.getInstance().wallet.createWallet(params)
    if (res) {
      if (!walletList.value) walletList.value = []
      walletList.value.push(res.wallet)

      if (!accountAPIStore.accountListByWalletKey[res.wallet.id]) {
        accountAPIStore.accountListByWalletKey[res.wallet.id] = []
      }
      if (res.account) {
        if (!accountAPIStore.accountListByWalletKey[res.wallet.id]) {
          accountAPIStore.accountListByWalletKey[res.wallet.id] = []
        }
        accountAPIStore.accountListByWalletKey[res.wallet.id]?.push(res.account)
      }
      //
      if (params.wallet.defaultWallet) {
        walletList.value = walletList.value.map((wallet) => {
          if (wallet.id !== res.wallet.id) {
            wallet.defaultWallet = false
          }
          return wallet
        })
      }
      return res
    }
  }

  const backupWallet = async (params: IBackupWalletRequest) => {
    const res = await WepinBackendAPI.getInstance().wallet.backupWallet(params)
    return res.mnemonic
  }

  const updateWallet = async (
    walletId: IWallet['id'],
    params: IUpdateWalletRequest,
  ) => {
    if (!walletList.value) return
    if (!walletId) return
    const res = await WepinBackendAPI.getInstance().wallet.updateWallet(
      { walletId },
      params,
    )
    if (res && walletList.value) {
      walletList.value = walletList.value.map((wallet) => {
        return wallet.id === res.wallet.id ? res.wallet : wallet
      })
      if (res.wallet.defaultWallet) {
        walletList.value = walletList.value.map((wallet) => {
          if (wallet.id !== res.wallet.id) {
            wallet.defaultWallet = false
          }
          return wallet
        })
      }
    }
    return res
  }

  const fetchWalletInfo = getExecuteOnce(
    async (walletId: IWallet['id'], force: boolean | undefined = false) => {
      if (!walletId) return
      if (force) {
        const res = await WepinBackendAPI.getInstance().wallet.getWalletInfo({
          walletId,
        })
        return res
      } else {
        if (!walletList.value) return
        return walletList.value.find((wallet) => wallet.id === walletId)
      }
    },
  )

  const fetchWalletList = getExecuteOnce(
    async (
      params?: IGetWalletListRequest,
      force: boolean | undefined = false,
    ) => {
      if (force || walletList.value?.length === 0) {
        try {
          const res =
            await WepinBackendAPI.getInstance().wallet.getWalletList(params)

          if (!res) {
            return []
          }

          walletList.value = res.wallets

          // 각 wallet에 accounts가 이미 포함되어 있음 - accountListByWalletKey에 저장
          if (res.wallets) {
            res.wallets.forEach((wallet: any) => {
              if (wallet.accounts && Array.isArray(wallet.accounts)) {
                // TypeAccount와 TokenAccount를 분리
                const typeAccounts: any[] = []
                const tokenAccounts: any[] = []

                wallet.accounts.forEach((account: any) => {
                  if (account.accountClassName === 'TypeAccount') {
                    // TypeAccount: 일반 계정 - 호환 필드 추가
                    const transformedAccount = {
                      ...account,
                      currency: account.currency,
                      coinId: null,
                      address: account.addresses?.[0] || '',
                      state: 'ACTIVE',
                      onchainState: 'CREATED',
                      defaultAccount: false,
                      createdTime: account.createdAt,
                    }
                    typeAccounts.push(transformedAccount)
                  } else if (account.accountClassName === 'TokenAccount') {
                    // TokenAccount: 토큰 계정 - 호환 필드 추가
                    const transformedAccount = {
                      ...account,
                      currency: account.currency,
                      accountId: account.parentAccountId,
                      tokenId: null,
                      state: 'ACTIVE',
                      onchainState: 'CREATED',
                      createdTime: account.createdAt,
                    }
                    tokenAccounts.push(transformedAccount)
                  }
                })

                // TypeAccount를 accountListByWalletKey에 저장
                accountAPIStore.accountListByWalletKey[wallet.id] = typeAccounts

                // TokenAccount를 parentAccountId별로 그룹화하여 accountTokenListByAccountId에 저장
                typeAccounts.forEach((typeAccount: any) => {
                  accountTokenAPIStore.accountTokenListByAccountId[
                    typeAccount.id
                  ] = []
                })
                tokenAccounts.forEach((tokenAccount: any) => {
                  const parentId = tokenAccount.parentAccountId
                  if (parentId) {
                    if (
                      !accountTokenAPIStore.accountTokenListByAccountId[
                        parentId
                      ]
                    ) {
                      accountTokenAPIStore.accountTokenListByAccountId[
                        parentId
                      ] = []
                    }
                    accountTokenAPIStore.accountTokenListByAccountId[
                      parentId
                    ].push(tokenAccount)
                  }
                })
              }
            })

            // 각 wallet의 key info 조회 (잠금 상태, 남은 시간 등)
            await Promise.all(
              res.wallets.map(async (wallet: any) => {
                try {
                  const keyInfo =
                    await WepinBackendAPI.getInstance().wallet.getWalletKeyInfo(
                      { id: wallet.id },
                    )
                  if (keyInfo) {
                    walletKeyInfoMap.value[wallet.id] = keyInfo
                  }
                } catch (error) {
                  // Key info fetch error ignored
                }
              }),
            )
          }
        } catch (error) {
          throw error
        }
      }

      return walletList.value
    },
  )

  const deleteWallet = async (
    walletId: IWallet['id'],
    params: IDeleteWalletRequest,
    options?: APIStoreDeleteWalletOptions,
  ) => {
    if (!walletId) return
    const res = await WepinBackendAPI.getInstance().wallet.deleteWallet(
      { walletId },
      params,
    )
    const deleteInMemory =
      typeof options?.deleteInMemory === 'undefined'
        ? true
        : options?.deleteInMemory
    if (deleteInMemory && res.result && walletList.value) {
      walletList.value = walletList.value.filter(
        (wallet) => wallet.id !== walletId,
      )
    }
    return res
  }
  function deleteWalletInMemory(walletId: IWallet['id']) {
    const index = walletList.value?.findIndex((val) => val.id === walletId)
    if (index) {
      walletList.value?.splice(index, 1)
    }
  }

  /**
   * user의 모든 wallet과 그 안의 account 목록을 조회하는 함수
   * wallet API가 이미 accounts, balance, currency 정보를 포함하므로 캐싱된 데이터 사용
   * 주의: 이 함수 호출 전에 반드시 fetchWalletList()를 먼저 호출해야 함
   */
  const fetchAllWalletDetail = async (force: boolean | undefined = false) => {
    // fetchWalletList로 이미 accounts를 캐싱했으므로 캐시된 데이터 사용
    await Promise.all(
      walletStore.walletList.map((wallet) => {
        return wallet.fetchAccounts(false)
      }),
    )

    await Promise.all(
      walletStore.accountList.map((account) => {
        return account.fetchAccountTokens(false)
      }),
    )

    if (force) {
      await Promise.all([
        walletStore.accountTokenList.map((accountToken) => {
          return coinAPIStore.fetchHeldCoinList({
            accountToken,
            force: false,
            locale: userStore.userInfo?.localeId,
          })
        }),
      ])
    }
    // Balance와 Coin price는 wallet API 응답에 이미 포함되어 있으므로 별도 호출하지 않음
  }

  // 계정 ID → 마지막 잔액 조회 시각 (refreshWalletBalances 중복 방지용)
  const BALANCE_REFRESH_TTL_MS = 30_000
  const balanceRefreshedAt = new Map<string, number>()

  /**
   * 지갑의 계정·토큰 잔액을 백엔드에서 새로 조회한다 (RPC 조회 후 DB 갱신).
   * wallet 목록 API 의 balance 는 DB 저장값이라, 이 호출이 없으면 새로 만든 계정·토큰은 0 으로 남는다.
   * 토큰 계정 ID 도 함께 보내야 백엔드가 토큰 잔액을 조회·갱신한다 (WV2-650)
   *
   * 지갑 진입 직후 계정 → 토큰 순으로 단계적으로 로드되며 호출이 연달아 들어오므로,
   * force 가 아니면 최근 BALANCE_REFRESH_TTL_MS 안에 조회한 계정은 다시 보내지 않는다 (RPC 중복 방지).
   * 실행 중인 호출을 그대로 재사용하면 나중에 로드된 토큰이 빠지므로 ID 단위로 거른다.
   */
  const refreshWalletBalances = async (
    walletId: IWallet['id'],
    force: boolean | undefined = false,
  ) => {
    const wallet = walletStore.getWallet(walletId)
    if (!wallet) return

    const accountTokenList = walletStore.accountTokenList.filter(
      (accountToken) => accountToken.wallet?.id === wallet.id,
    )
    const accountIds = [
      ...wallet.accounts.map((account) => account.id),
      ...accountTokenList.map((accountToken) => accountToken.id),
    ]
    const now = Date.now()
    const targetIds = force
      ? accountIds
      : accountIds.filter(
          (id) =>
            now - (balanceRefreshedAt.get(id) ?? 0) >= BALANCE_REFRESH_TTL_MS,
        )
    if (targetIds.length === 0) return
    // await 전에 기록해야 동시에 들어온 호출이 같은 계정을 다시 보내지 않는다
    targetIds.forEach((id) => balanceRefreshedAt.set(id, now))

    const accountBalanceStore = useAccountBalanceAPIStore()
    await accountBalanceStore.fetchAccountBalances(targetIds)
  }

  const fetchWalletDetail = async (force: boolean | undefined = false) => {
    const wallet = walletStore.getWallet(walletStore.selectedWalletId)
    if (!wallet) return

    await wallet.fetchAccounts(force)
    await Promise.all(
      wallet.accounts.map((account) => {
        return account.fetchAccountTokens(force)
      }),
    )
    const accountTokenList = walletStore.accountTokenList.filter(
      (accountToken) => accountToken.wallet?.id === wallet.id,
    )
    await Promise.all([
      accountTokenList.map((accountToken) => {
        return coinAPIStore.fetchHeldCoinList({
          accountToken,
          force,
          locale: userStore.userInfo?.localeId,
        })
      }),
    ])

    // V2: 모든 account의 balance를 한 번에 조회
    await refreshWalletBalances(wallet.id, force)

    useConditionalFetch(
      async () => {
        await coinAPIStore.fetchCoinList({
          coinIds: [],
          force: true,
          needToAddData: true,
          locale: userStore.userInfo?.localeId,
          reset: true,
        })
      },
      computed<boolean>(() => !!userStore.userInfo?.localeId),
    )

    useConditionalFetch(
      () =>
        coinPriceAPIStore.fetchCoinPrice({
          coinGeckoIds: makeCoinGeckoIds([
            ...wallet.accounts,
            ...accountTokenList,
          ]),
          currency: infoAPIStore.currentCurrency,
        }),
      computed<boolean>(() => {
        return (
          !wallet.accounts.some((account) => account.coin === undefined) &&
          !accountTokenList.some(
            (accountToken) => accountToken.coin === undefined,
          )
        )
      }),
    )
  }

  const makeCoinGeckoIds = (accountList: BaseAccount[]): string[] => {
    return accountList
      .map((account) => account.coin?.coinGeckoId)
      .filter((acc) => acc) as string[]
  }

  const fetchWalletCoreKeyInfo = async (walletId: IWallet['id']) => {
    if (!walletId) return
    const res = await WepinBackendAPI.getInstance().wallet.getWalletCoreKeyInfo(
      { id: walletId },
    )
    if (!res) return
    return res
  }

  const fetchWalletKeyInfo = async (walletId: IWallet['id']) => {
    if (!walletId) return
    const res = await WepinBackendAPI.getInstance().wallet.getWalletKeyInfo({
      id: walletId,
    })
    if (!res) return
    return res
  }

  const resetPinTryCount = async (walletId: IWallet['id']) => {
    if (!walletId) return
    const res = await WepinBackendAPI.getInstance().wallet.resetPinTryCount({
      id: walletId,
    })
    if (!res) return
    return res
  }

  const verifyPin = async (
    walletId: IWallet['id'],
    UVD: {
      b64SKey: string
      b64Data: string
    },
  ) => {
    if (!walletId) return
    const res = await WepinBackendAPI.getInstance().wallet.verifyPin({
      walletId: walletId,
      UVD: UVD,
    })
    if (!res) return
    return res
  }

  const updateWalletState = async (walletId: IWallet['id']) => {
    if (!walletId) return
    const res = await WepinBackendAPI.getInstance().wallet.updateWalletState({
      walletId: walletId,
    })
    if (!res) return
    return res
  }

  const changePin = async (params: IChangePinRequest) => {
    if (!params.walletId) return
    const res = await WepinBackendAPI.getInstance().wallet.changePin(params)
    if (!res) return
    return res
  }

  const reset = () => {
    walletList.value = []
    walletKeyInfoMap.value = {}
    balanceRefreshedAt.clear()
  }

  // 특정 wallet의 key info 가져오기
  const getWalletKeyInfo = (walletId: string): IWalletKeyInfo | undefined => {
    return walletKeyInfoMap.value[walletId]
  }

  return {
    walletList,
    walletKeyInfoMap,
    getWalletKeyInfo,
    createWallet,
    backupWallet,
    updateWallet,
    fetchWalletInfo,
    fetchWalletList,
    deleteWallet,
    deleteWalletInMemory,
    fetchAllWalletDetail,
    fetchWalletDetail,
    refreshWalletBalances,
    fetchWalletCoreKeyInfo,
    fetchWalletKeyInfo,
    reset,
    resetPinTryCount,
    verifyPin,
    updateWalletState,
    changePin,
  }
})

export { useWalletAPIStore }
